import { useGoogleLogin } from '@react-oauth/google';
import { useEffect, useState } from 'react';
import AppleLogin from 'react-apple-login';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../service/AuthService';
import { LocalStorageService } from '../../service/LocalStorageService';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';

type Props = {
  text?: string;
  type?: 'login' | 'register';
  loginCallback?: () => Promise<void>;
};

type AppleAuthorization = {
  id_token: string;
};

type AppleUser = {
  name?: {
    firstName?: string;
  };
};

type AppleLoginData = {
  authorization?: AppleAuthorization;
  user?: AppleUser;
};

const BrandsLogin = ({
  text = 'Or login with',
  type = 'login',
  loginCallback,
}: Props) => {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore(state => state.setAccessToken);
  const { setNotification } = useNotificationStore(state => state);
  const [isError, setIsError] = useState('');
  const [token, setToken] = useState('');

  const login = useGoogleLogin({
    onSuccess: tokenResponse =>
      googleLoginAndRegister(tokenResponse.access_token, type),
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setToken(token);
    }
  }, []);

  const completeAuth = async (accessToken: string) => {
    LocalStorageService.setItem('accessToken', accessToken);
    setAccessToken(accessToken);
    if (loginCallback) await loginCallback();
    navigate('/');
  };

  const handleAppleLogin = async (data: AppleLoginData) => {
    try {
      const idToken = data.authorization?.id_token;
      const name = data.user?.name?.firstName ?? '';

      if (idToken) {
        if (type === 'register') {
          const response = await AuthService.registerWithApple(
            { accessToken: idToken, name },
            token,
          );
          await completeAuth(response.data.accessToken);
        } else if (type === 'login') {
          try {
            const response = await AuthService.loginWithApple({ accessToken: idToken });
            await completeAuth(response.data.accessToken);
          } catch (error) {
            const loginError = error as { message?: string };
            if (loginError.message === 'Account not found!') {
              const response = await AuthService.registerWithApple(
                { accessToken: idToken, name },
                token,
              );
              await completeAuth(response.data.accessToken);
              setNotification({
                type: 'success',
                message: 'Your account was created with Apple and you are now signed in.',
              });
              return;
            }
            throw error;
          }
        }
      }
    } catch (error) {
      const socialError = error as { message?: string };
      if (socialError.message === 'Account not found!') {
        setIsError('Account not found! Please go to register page.');
      } else if (socialError.message === 'Email already exists!') {
        setIsError('Email already exists! please go to login page.');
      } else if (socialError.message === 'This account is not registered with Apple!') {
        setIsError('This account is not registered with Apple!');
      } else if (socialError.message) {
        setNotification({ type: 'error', message: socialError.message });
      }
    }
  };

  const googleLoginAndRegister = async (accessToken: string, type: 'login' | 'register') => {
    try {
      if (type === 'register') {
        const response = await AuthService.registerWithGoogle(accessToken, token);
        await completeAuth(response.data.accessToken);
      } else if (type === 'login') {
        try {
          const response = await AuthService.loginWithGoogle(accessToken);
          await completeAuth(response.data.accessToken);
        } catch (error) {
          const loginError = error as { message?: string };
          if (loginError.message === 'Account not found!') {
            const response = await AuthService.registerWithGoogle(accessToken, token);
            await completeAuth(response.data.accessToken);
            setNotification({
              type: 'success',
              message: 'Your account was created with Google and you are now signed in.',
            });
            return;
          }
          throw error;
        }
      }
    } catch (error) {
      const socialError = error as { message?: string };
      if (socialError.message === 'Account not found!') {
        setNotification({ type: 'error', message: 'Account not found! Please go to register page.' });
      } else if (socialError.message === 'User already exists!') {
        setIsError('User already exists! Please go to login page.');
      } else if (socialError.message === 'This account is not registered with Google!') {
        setIsError('This account is not registered with Google!');
      } else if (socialError.message) {
        setNotification({ type: 'error', message: socialError.message });
      }
    }
  };

  return (
    <div className="brands-login">
      <div className="brands-login__divider">
        <div className="brands-login__line" />
        <span className="brands-login__text">{text}</span>
        <div className="brands-login__line" />
      </div>

      <div className="brands-login__buttons">
        <button type="button" onClick={() => login()} className="brands-login__btn">
          <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Google
        </button>

        <AppleLogin
          render={renderProps => (
            <button type="button" onClick={renderProps.onClick} className="brands-login__btn">
              <svg viewBox="0 0 814 1000" width="14" height="17" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-58.5-81.5-105.9-207-105.9-326.5 0-186.8 121.3-286.3 240.8-286.3 63.5 0 116.3 41.6 156 41.6 37.6 0 96.2-44.2 168.4-44.2 27.1 0 124.4 2.5 188.7 96.3zM554.1 159.4c31.1-36.9 53.1-88.1 53.1-139.4 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8.7 15.6 1.3 18.2 2.5.6 6.4.6 10.2.6 45.8 0 103.7-30.4 139.5-70.6z" fill="#000" />
              </svg>
              Apple
            </button>
          )}
          usePopup={window.location.hostname !== 'localhost'}
          scope="email name"
          clientId={import.meta.env.VITE_APPLE_CLIENT_ID || ''}
          redirectURI={`${window.location.origin}/${type}`}
          callback={handleAppleLogin}
        />
      </div>

      {isError && <p className="brands-login__error">{isError}</p>}
    </div>
  );
};

export default BrandsLogin;
